<template>
  <Teleport to="body">
    <Transition name="bottom-sheet">
      <div v-if="modelValue" class="bottom-sheet-overlay" @click.self="handleClose">
        <div class="bottom-sheet" :class="{ 'bottom-sheet--auto': autoHeight, 'bottom-sheet--no-top-fade': hideHeader }" :style="{ maxHeight: maxHeight }">
          <!-- Fixed buttons and title -->
          <Transition name="fade">
            <div v-if="!hideHeader" class="bottom-sheet-buttons" :class="{ 'bottom-sheet-buttons--blurred': headerBlurred }">
              <button v-if="!hideConfirm" class="header-btn close-btn" @click="handleClose">
                <v-icon size="22">mdi-close</v-icon>
              </button>
              <div v-else class="header-btn" style="visibility: hidden" />
              <div class="header-title">
                <slot name="title"></slot>
              </div>
              <button v-if="!hideConfirm" class="header-btn confirm-btn" @click="handleConfirm" :disabled="confirmDisabled">
                <v-icon size="22" color="white">mdi-check</v-icon>
              </button>
              <button v-else class="header-btn close-btn" @click="handleClose">
                <v-icon size="22">mdi-close</v-icon>
              </button>
            </div>
          </Transition>

          <!-- Floating close button (when header hidden) -->
          <button v-if="hideHeader && !hideFloatingClose" class="floating-close-btn header-btn close-btn" @click="handleClose">
            <v-icon size="22">mdi-close</v-icon>
          </button>

          <!-- Top fade (only when scrolled) -->
          <Transition name="fade-slow">
            <div v-if="showTopFade" class="sheet-fade-top" />
          </Transition>

          <!-- Scrollable area -->
          <div class="bottom-sheet-scroll" :class="{ 'no-header-padding': hideHeader }" ref="scrollEl" @scroll="onScroll">
            <!-- Content -->
            <div class="bottom-sheet-content" :class="{ 'no-padding': noPadding }">
              <slot></slot>
            </div>
          </div>

          <!-- Bottom fade -->
          <Transition name="fade">
            <div v-if="showBottomFade && !hideBottomFade" class="sheet-fade-bottom" />
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { watch, toRefs, ref, nextTick, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  maxHeight: {
    type: String,
    default: '92vh'
  },
  confirmDisabled: {
    type: Boolean,
    default: false
  },
  hideHeader: {
    type: Boolean,
    default: false
  },
  hideConfirm: {
    type: Boolean,
    default: false
  },
  noPadding: {
    type: Boolean,
    default: false
  },
  headerBlurred: {
    type: Boolean,
    default: false
  },
  autoHeight: {
    type: Boolean,
    default: false
  },
  hideFloatingClose: {
    type: Boolean,
    default: false
  },
  hideBottomFade: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'close', 'confirm']);

const { modelValue } = toRefs(props);

const scrollEl = ref<HTMLElement | null>(null);
const showBottomFade = ref(true);
const showTopFade = ref(false);

const checkScroll = () => {
  const el = scrollEl.value;
  if (!el) return;
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
  showBottomFade.value = !atBottom;
  showTopFade.value = el.scrollTop > 8;
};

const onScroll = () => checkScroll();

let savedScrollY = 0;
let scrollLockListener: ((e: TouchEvent) => void) | null = null;

watch(modelValue, async (open) => {
  if (open) {
    await nextTick();
    checkScroll();
    savedScrollY = window.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    // Block background scroll on iOS without position:fixed (which breaks input focus)
    scrollLockListener = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow scrolling inside the bottom-sheet-scroll area
      if (target.closest('.bottom-sheet-scroll')) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', scrollLockListener, { passive: false });
  } else {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (scrollLockListener) {
      document.removeEventListener('touchmove', scrollLockListener);
      scrollLockListener = null;
    }
  }
});

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};

onBeforeUnmount(() => {
  if (modelValue.value) {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (scrollLockListener) {
      document.removeEventListener('touchmove', scrollLockListener);
      scrollLockListener = null;
    }
  }
});

const handleConfirm = () => {
  emit('confirm');
};
</script>

<style lang="scss" scoped>
.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9999;
}

.bottom-sheet {
  position: relative;
  width: 100%;
  max-width: 600px;
  border-radius: 32px 32px 0 0;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-bottom: none;
  background: rgb(var(--v-theme-sheetBackground));
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  box-shadow: 
    0 -8px 60px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(var(--v-theme-sheetText), 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 92vh;
  
  &--auto {
    height: auto;
  }
  
  &:not(.bottom-sheet--no-top-fade)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to bottom, rgb(var(--v-theme-sheetBackground)) 0%, rgb(var(--v-theme-sheetBackground)) 30%, transparent 100%);
    z-index: 5;
    pointer-events: none;
    border-radius: 32px 32px 0 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(232, 0, 45, 0.04) 0%, transparent 40%, rgba(0, 212, 255, 0.04) 100%);
    pointer-events: none;
    border-radius: 32px 32px 0 0;
    z-index: 0;
  }
}

.bottom-sheet-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: 50px;
  
  &.no-header-padding {
    padding-top: 0;
  }
}

.bottom-sheet-buttons {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  pointer-events: none;
  border-radius: 16px;
  padding: 4px;
  transition: background 0.25s ease, backdrop-filter 0.25s ease;
  
  .header-btn {
    pointer-events: auto;
  }
}

.bottom-sheet-buttons--blurred {
  background: rgba(var(--v-theme-sheetBackground), 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.header-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-top: 3px;
}

.header-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
}

.close-btn {
  background: rgba(var(--v-theme-sheetCloseBtn), 0.65);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-color: transparent;
  color: rgb(var(--v-theme-sheetText));
  
  &:hover {
    filter: brightness(0.92);
  }
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
}

.confirm-btn {
  background: rgb(var(--v-theme-success));
  border-color: transparent;
  color: #ffffff;
  box-shadow: 
    0 4px 12px rgba(var(--v-theme-success), 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  
  &:hover {
    filter: brightness(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: grayscale(0.5);
  }
  
  :deep(.v-icon) {
    color: #ffffff !important;
  }
}

.bottom-sheet-content {
  padding: 20px 20px 20px;

  &.no-padding {
    padding: 0;
  }
}

.floating-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.sheet-fade-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  background: linear-gradient(to bottom, rgb(var(--v-theme-sheetBackground)) 0%, transparent 100%);
  pointer-events: none;
  z-index: 5;
  border-radius: 32px 32px 0 0;
}

.sheet-fade-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(to top, rgb(var(--v-theme-sheetBackground)) 0%, rgb(var(--v-theme-sheetBackground)) 10%, transparent 100%);
  pointer-events: none;
  z-index: 4;
}

/* Fade transition for header */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slow fade for top shadow */
.fade-slow-enter-active {
  transition: opacity 0.5s ease-in;
}
.fade-slow-leave-active {
  transition: opacity 0.35s ease-out;
}
.fade-slow-enter-from,
.fade-slow-leave-to {
  opacity: 0;
}

/* Transition */
.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity 0.3s ease;
}

.bottom-sheet-enter-active .bottom-sheet,
.bottom-sheet-leave-active .bottom-sheet {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  opacity: 0;
}

.bottom-sheet-enter-from .bottom-sheet {
  transform: translateY(100%);
}

.bottom-sheet-leave-to .bottom-sheet {
  transform: translateY(100%);
}
</style>
